import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CommonTableNew from "../../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import {
  getData,
  postData,
} from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { useNavigate } from "react-router-dom";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../Utils";
import MonthDropdown from "../../CommonCompo/MonthDropdown";
import { toast } from "react-toastify";

type LeaveRow = {
  LeaveReqId: string | number;
  EmployeeId?: string | number;
  EmployeeCode?: string;
  EmployeeName?: string;
  Designation?: string;
  BranchName?: string;
  TerritoryName?: string;
  LeaveType?: string;
  LeaveDays?: number | string;
  LeaveFrom?: string;
  LeaveTo?: string;
  LeaveStatus?: string | number;
  LeaveReason?: string;
  Attachment?: string;
};

const LeaveApprovals: React.FC = () => {
  const navigate = useNavigate();
  const serverUrl = localStorage.getItem("serverUrl");

  const [loading, setLoading] = useState<boolean>(true);
  const [approving, setApproving] = useState<boolean>(false);
  const [leaveData, setLeaveData] = useState<LeaveRow[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [statusData, setStatusData] = useState<Record<string, string>>({});
  const [selectedStatusValue, setSelectedStatusValue] = useState<string>("1");
  const [selectedLeaveIds, setSelectedLeaveIds] = useState<string[]>([]);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
  const [pendingApproveIds, setPendingApproveIds] = useState<string[]>([]);

  const getEmployeeName = (item: any) =>
    `${item?.EmployeeName || item?.name || item?.FirstName || ""} ${item?.LastName || ""}`.trim();

  const getDesignation = (item: any) =>
    item?.Designation ||
    item?.DesignationName ||
    item?.Designations?.Designation ||
    "";

  const getBranchName = (item: any) =>
    item?.BranchName || item?.Branch?.Branchname || item?.Branch?.BranchName || "";

  const getTerritoryName = (item: any) =>
    item?.TerritoryName || item?.territory_name || "";

  const getStatusLabel = (status: string | number | undefined): string => {
    if (status === undefined || status === null) return "-";
    const found = Object.entries(statusData).find(
      ([, value]) => String(value) === String(status),
    );
    return found?.[0] || String(status);
  };

  const isRaisedStatus = (status: string | number | undefined) =>
    getStatusLabel(status).toLowerCase() === "raised" || String(status) === "1";

  const getStatusBadgeStyle = (status: string | number | undefined) => {
    const label = getStatusLabel(status).toLowerCase();
    if (label === "raised") {
      return { backgroundColor: "#e96900", color: "#fff" };
    }
    if (label === "approve" || label === "approved") {
      return { backgroundColor: "#2e8b57", color: "#fff" };
    }
    if (label === "reject" || label === "rejected") {
      return { backgroundColor: "#c0392b", color: "#fff" };
    }
    if (label === "cancel" || label === "cancelled") {
      return { backgroundColor: "#6c757d", color: "#fff" };
    }
    return { backgroundColor: "#0d6efd", color: "#fff" };
  };

  const getLeaveTypeStyle = (leaveType?: string) => {
    const type = String(leaveType || "").toLowerCase();
    if (type.includes("casual")) return { backgroundColor: "#1f6feb", color: "#fff" };
    if (type.includes("sick")) return { backgroundColor: "#8b5cf6", color: "#fff" };
    if (type.includes("privilege")) return { backgroundColor: "#16a34a", color: "#fff" };
    if (type.includes("earned")) return { backgroundColor: "#0f766e", color: "#fff" };
    return { backgroundColor: "#495057", color: "#fff" };
  };

  const formatDateToDDMMMYYYY = (dateStr?: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(d);
  };

  const extractRequestIds = (item: any): string[] => {
    const keys = [
      "LeaveReqId",
      "leave_req_id",
      "leave_request_id",
      "leave__request_id",
      "LeaveRequestId",
      "LeaveReqIds",
      "LeaveRequestIds",
      "ReqId",
      "req_id",
    ];
    const ids: string[] = [];

    keys.forEach((k) => {
      const value = item?.[k];
      if (value === undefined || value === null || value === "") return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && String(v).trim()) {
            ids.push(String(v).trim());
          }
        });
        return;
      }
      String(value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => ids.push(v));
    });

    return Array.from(new Set(ids));
  };

  const getMonthDateRange = (monthValue: string) => {
    if (!monthValue) return null;
    const match = String(monthValue).match(/^(\d{2})-(\d{4})$/);
    if (!match) return null;
    const month = Number(match[1]);
    const year = Number(match[2]);
    if (month < 1 || month > 12) return null;
    const lastDate = new Date(year, month, 0).getDate();
    return {
      from_date: `${year}-${String(month).padStart(2, "0")}-01`,
      to_date: `${year}-${String(month).padStart(2, "0")}-${String(lastDate).padStart(2, "0")}`,
    };
  };

  const toLeaveRow = (item: any, req: any): LeaveRow => ({
    LeaveReqId:
      req?.LeaveReqId ??
      req?.leave__request_id ??
      req?.leave_request_id ??
      item?.LeaveReqId ??
      item?.leave__request_id,
    EmployeeId:
      req?.EmployeeId ??
      item?.EmployeeId ??
      req?.employee_id ??
      item?.employee_id,
    EmployeeCode: req?.EmployeeCode ?? item?.EmployeeCode ?? item?.EmpCode,
    EmployeeName: getEmployeeName({
      ...item,
      ...req,
      EmployeeName: req?.EmployeeName || item?.EmployeeName,
    }),
    Designation: getDesignation({ ...item, ...req }),
    BranchName: getBranchName({ ...item, ...req }),
    TerritoryName: getTerritoryName({ ...item, ...req }),
    LeaveType: req?.LeaveType ?? item?.LeaveType,
    LeaveDays: req?.LeaveDays ?? item?.LeaveDays,
    LeaveFrom: req?.LeaveFrom ?? item?.LeaveFrom,
    LeaveTo: req?.LeaveTo ?? item?.LeaveTo,
    LeaveStatus: req?.LeaveStatus ?? req?.Status ?? item?.LeaveStatus ?? item?.Status,
    LeaveReason: req?.LeaveReason ?? item?.LeaveReason,
    Attachment: req?.Attachment ?? item?.Attachment,
  });

  const normalizeLeaveRows = (payload: any): LeaveRow[] => {
    const baseRows = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.LeaveRequest)
          ? payload.LeaveRequest
          : [];

    const out: LeaveRow[] = [];

    baseRows.forEach((item: any) => {
      const directReqIds = extractRequestIds(item);
      if (directReqIds.length && (item?.LeaveType || item?.LeaveFrom || item?.LeaveTo)) {
        directReqIds.forEach((id) => {
          out.push(
            toLeaveRow(item, {
              ...item,
              LeaveReqId: id,
            }),
          );
        });
        return;
      }

      const reqList = Array.isArray(item?.LeaveRequest)
        ? item.LeaveRequest
        : Array.isArray(item?.Leaves)
          ? item.Leaves
          : [];

      reqList.forEach((req: any) => {
        const reqIds = extractRequestIds(req);
        const fallbackId = req?.LeaveReqId ?? req?.leave_request_id ?? req?.leave__request_id;
        const finalIds = reqIds.length ? reqIds : fallbackId ? [String(fallbackId)] : [];
        finalIds.forEach((id) => {
          out.push(
            toLeaveRow(item, {
              ...req,
              LeaveReqId: id,
            }),
          );
        });
      });
    });

    const uniqueMap = new Map<string, LeaveRow>();
    out.forEach((row) => {
      uniqueMap.set(String(row.LeaveReqId), row);
    });
    return Array.from(uniqueMap.values());
  };

  const enrichLeavesFromEmployeeApi = async (baseRows: any[]): Promise<LeaveRow[]> => {
    if (!serverUrl || !baseRows.length) return [];

    const monthRange = getMonthDateRange(selectedMonth);
    const nowYear = new Date().getFullYear();
    const defaultFrom = `${nowYear - 1}-01-01`;
    const defaultTo = `${nowYear}-12-31`;

    const leaveRowsByEmp = await Promise.all(
      baseRows.map(async (item: any) => {
        const reqIds = extractRequestIds(item);
        const tryParams: Record<string, string>[] = [];

        const employeeId =
          item?.EmployeeId ?? item?.employee_id ?? item?.EmpId ?? item?.emp_id;
        const employeeCode =
          item?.EmployeeCode ?? item?.employee_code ?? item?.EmpCode ?? item?.emp_code;

        const dateParams = {
          from_date: monthRange?.from_date ?? defaultFrom,
          to_date: monthRange?.to_date ?? defaultTo,
        };

        if (employeeId) {
          tryParams.push({ ...dateParams, employee_id: String(employeeId) });
        }
        if (employeeCode) {
          tryParams.push({ ...dateParams, employee_code: String(employeeCode) });
          tryParams.push({ ...dateParams, emp_code: String(employeeCode) });
        }

        for (const params of tryParams) {
          try {
            const detailUrl = `${serverUrl}${API.getEmployeeLeave}`;
            const { status, data } = await getData(detailUrl, true, params);
            const payload = data?.data ?? data;
            if (status !== 200 || !Array.isArray(payload?.LeaveRequest)) continue;

            let requests = payload.LeaveRequest;
            if (reqIds.length) {
              const reqIdSet = new Set(reqIds.map(String));
              requests = requests.filter((req: any) =>
                reqIdSet.has(
                  String(
                    req?.LeaveReqId ?? req?.leave_request_id ?? req?.leave__request_id,
                  ),
                ),
              );
            }

            return requests.map((req: any) => toLeaveRow(item, req));
          } catch (err) {
            console.error("Failed leave detail fetch", err);
          }
        }
        return [];
      }),
    );

    const merged = leaveRowsByEmp.flat();
    const uniqueMap = new Map<string, LeaveRow>();
    merged.forEach((row) => uniqueMap.set(String(row.LeaveReqId), row));
    return Array.from(uniqueMap.values());
  };

  const fetchLeaveStatusMap = async () => {
    if (!serverUrl) return;
    try {
      const url = `${serverUrl}${API.getLeaveStatus}`;
      const { status, data } = await getData(url, true);
      const payload = data?.data ?? data;
      if (status === 200 && payload) {
        setStatusData(payload);
        const raisedValue = String(payload?.Raised ?? "1");
        if (!selectedStatusValue) {
          setSelectedStatusValue(raisedValue);
        }
      }
    } catch (err) {
      console.error("Failed fetching leave status map", err);
    }
  };

  const fetchPendingLeaves = async () => {
    if (!serverUrl) return;
    setLoading(true);
    try {
      const url = `${serverUrl}${API.pendingAdminLeaveCountList}`;
      const params: Record<string, string> = {};
      if (selectedMonth) params.moye = selectedMonth;

      const { status, data } = await getData(url, true, params);
      const ok = status === 200 || data?.statusCode === 200;
      const payload = data?.data ?? data;
      const baseRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      let normalized = ok ? normalizeLeaveRows(payload) : [];

      if (ok && baseRows.length > 0) {
        const enriched = await enrichLeavesFromEmployeeApi(baseRows);
        if (enriched.length > 0) {
          normalized = enriched;
        }
      }

      setLeaveData(normalized);
      setSelectedLeaveIds([]);
    } catch (err) {
      console.error("Failed to fetch leaves", err);
      setLeaveData([]);
      setSelectedLeaveIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveStatusMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverUrl]);

  useEffect(() => {
    fetchPendingLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverUrl, selectedMonth, selectedStatusValue]);

  const filteredData = useMemo(() => {
    const selectedStatusLabel = selectedStatusValue
      ? getStatusLabel(selectedStatusValue).toLowerCase()
      : "";

    let rows = leaveData;
    if (selectedStatusLabel) {
      rows = rows.filter((item) => {
        const rowStatusLabel = getStatusLabel(item.LeaveStatus).toLowerCase();
        return rowStatusLabel === selectedStatusLabel;
      });
    }

    if (!searchTerm.trim()) return rows;
    const search = searchTerm.toLowerCase();
    return rows.filter((item) => {
      const employeeName = String(item.EmployeeName || "").toLowerCase();
      const empCode = String(item.EmployeeCode || "").toLowerCase();
      const leaveType = String(item.LeaveType || "").toLowerCase();
      const leaveReason = String(item.LeaveReason || "").toLowerCase();
      return (
        employeeName.includes(search) ||
        empCode.includes(search) ||
        leaveType.includes(search) ||
        leaveReason.includes(search)
      );
    });
  }, [leaveData, searchTerm, selectedStatusValue, statusData]);

  const raisedRows = useMemo(
    () => filteredData.filter((row) => isRaisedStatus(row.LeaveStatus)),
    [filteredData],
  );

  const allRaisedSelected =
    raisedRows.length > 0 &&
    raisedRows.every((row) => selectedLeaveIds.includes(String(row.LeaveReqId)));

  const toggleSelectAllRaised = () => {
    if (allRaisedSelected) {
      const raisedIdSet = new Set(raisedRows.map((r) => String(r.LeaveReqId)));
      setSelectedLeaveIds((prev) => prev.filter((id) => !raisedIdSet.has(id)));
      return;
    }
    const merged = new Set(selectedLeaveIds);
    raisedRows.forEach((row) => merged.add(String(row.LeaveReqId)));
    setSelectedLeaveIds(Array.from(merged));
  };

  const toggleSelectLeave = (leaveReqId: string | number) => {
    const id = String(leaveReqId);
    setSelectedLeaveIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const approveLeaves = async (leaveIds: string[]) => {
    if (!leaveIds.length || !serverUrl) return;
    setApproving(true);
    try {
      const url = `${serverUrl}${API.leaveApprove}`;
      const { status, data } = await postData(url, true, {
        leave__request_id: leaveIds.join(","),
      });
      const ok = status === 200 || data?.statusCode === 200;
      if (ok) {
        toast.success(data?.message || "Leave approved");
        await fetchPendingLeaves();
      } else {
        toast.error(data?.message || "Approve failed");
      }
    } catch (err) {
      console.error("Leave approval failed", err);
      toast.error("Approve failed");
    } finally {
      setApproving(false);
    }
  };

  const openApproveModal = (leaveIds: string[]) => {
    if (!leaveIds.length) return;
    setPendingApproveIds(leaveIds);
    setApproveModalOpen(true);
  };

  const closeApproveModal = () => {
    if (approving) return;
    setApproveModalOpen(false);
    setPendingApproveIds([]);
  };

  const openLeaveDashboard = (row: LeaveRow) => {
    navigate("/leaveDashboard", {
      state: {
        empId: row.EmployeeId,
        name: row.EmployeeName,
        TerritoryName: row.TerritoryName,
        EmployeeCode: row.EmployeeCode,
        Designation: row.Designation,
        backPath: "/Approvals",
        backState: { activeTab: "2" },
      },
    });
  };

  const headData = [
    {
      id: "0",
      head: (
        <input
          type="checkbox"
          checked={allRaisedSelected}
          onChange={toggleSelectAllRaised}
          title="Select all raised leave requests"
          disabled={raisedRows.length === 0}
        />
      ),
      key: "Select",
      sortKey: null,
    },
    { id: "1", head: "Employee", key: "EmployeeName", sortKey: "EmployeeName" },
    { id: "2", head: "LeaveType", key: "LeaveType", sortKey: "LeaveType" },
    { id: "3", head: "LeaveDays", key: "LeaveDays", sortKey: "LeaveDays" },
    { id: "4", head: "LeaveFrom-LeaveTo", key: "LeaveFromLeaveTo", sortKey: null },
    { id: "5", head: "LeaveStatus", key: "LeaveStatus", sortKey: "LeaveStatus" },
    { id: "6", head: "LeaveReason", key: "LeaveReason", sortKey: null },
    { id: "7", head: "Attachment", key: "Attachment", sortKey: null },
    { id: "8", head: "Action", key: "Action", sortKey: null },
  ];

  return (
    <div className="px-2">
      <Container fluid>
        <Col xxl={12}>
          <div className="d-flex justify-content-between align-items-center mb-3 py-2 gap-2 flex-wrap">
            <div style={{ maxWidth: "320px", minWidth: "280px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search by employee, code, leave type, reason"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
              <select
                className="form-select"
                value={selectedStatusValue}
                onChange={(e) => setSelectedStatusValue(e.target.value)}
                style={{ width: "200px" }}
              >
                {Object.entries(statusData).map(([label, value]) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </select>

              <div style={{ minWidth: "170px" }}>
                <MonthDropdown onChange={(value) => setSelectedMonth(value)} />
              </div>

              <Button
                color="success"
                size="sm"
                disabled={approving || selectedLeaveIds.length === 0}
                onClick={() => openApproveModal(selectedLeaveIds)}
              >
                {approving ? "Approving..." : `Bulk Approve (${selectedLeaveIds.length})`}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-3">
              <Image
                className="b-r-10 img-80"
                src={dynamicImage("dashboard/loader.gif")}
                alt="Loading..."
              />
            </div>
          ) : (
            <CommonTableNew
              caption=""
              headData={headData}
              data={filteredData}
              renderRow={(row: LeaveRow, rowIndex: number) => {
                const raised = isRaisedStatus(row.LeaveStatus);
                const rowId = String(row.LeaveReqId);
                const checked = selectedLeaveIds.includes(rowId);
                return (
                  <tr key={`${rowId}-${rowIndex}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!raised}
                        onChange={() => toggleSelectLeave(row.LeaveReqId)}
                      />
                    </td>
                    <td>
                      <div>{row.EmployeeName || "-"}</div>
                      <div className="text-muted small">{row.EmployeeCode || "-"}</div>
                    </td>
                    <td>
                      <span
                        style={{
                          ...getLeaveTypeStyle(row.LeaveType),
                          borderRadius: 14,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          display: "inline-block",
                          minWidth: 80,
                          textAlign: "center",
                        }}
                      >
                        {row.LeaveType || "-"}
                      </span>
                    </td>
                    <td>{row.LeaveDays ?? "-"}</td>
                    <td>
                      {formatDateToDDMMMYYYY(row.LeaveFrom)} to{" "}
                      {formatDateToDDMMMYYYY(row.LeaveTo)}
                    </td>
                    <td>
                      <span
                        style={{
                          ...getStatusBadgeStyle(row.LeaveStatus),
                          borderRadius: 14,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          display: "inline-block",
                          minWidth: 85,
                          textAlign: "center",
                        }}
                      >
                        {getStatusLabel(row.LeaveStatus)}
                      </span>
                    </td>
                    <td>{row.LeaveReason || "-"}</td>
                    <td>
                      {row.Attachment ? (
                        <a
                          href={
                            String(row.Attachment).startsWith("http")
                              ? row.Attachment
                              : `${serverUrl}media?id=${row.Attachment}`
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        <Button
                          color="success"
                          size="sm"
                          disabled={!raised || approving}
                          onClick={() => openApproveModal([rowId])}
                        >
                          Approve
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => openLeaveDashboard(row)}
                        >
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }}
            />
          )}
        </Col>
      </Container>

      <Modal isOpen={approveModalOpen} toggle={closeApproveModal}>
        <ModalHeader toggle={closeApproveModal}>Confirm Approval</ModalHeader>
        <ModalBody>
          Are you sure you want to approve {pendingApproveIds.length} leave
          {pendingApproveIds.length > 1 ? "s" : ""}?
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            disabled={approving}
            onClick={async () => {
              await approveLeaves(pendingApproveIds);
              setApproveModalOpen(false);
              setPendingApproveIds([]);
            }}
          >
            {approving ? "Approving..." : "Yes, Approve"}
          </Button>
          <Button color="secondary" onClick={closeApproveModal} disabled={approving}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default LeaveApprovals;
